import {
  Component,
  input,
  output,
  signal,
  computed,
  effect,
  viewChild,
  ElementRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { MatMenuTrigger } from '@angular/material/menu';

export type M3SplitButtonVariant = 'elevated' | 'filled' | 'tonal' | 'outlined';
export type M3SplitButtonSize = 'xs' | 'sm' | 'default' | 'lg' | 'xl';

@Component({
  selector: 'app-m3-split-button',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div [class]="containerClass()">
      <button
        type="button"
        class="leading-button"
        [class.pressing]="isLeadingPressing()"
        [class.press-release]="isLeadingReleasing()"
        [disabled]="disabled()"
        [attr.aria-label]="ariaLabel() || label()"
        (click)="onPrimaryClick($event)"
        (mousedown)="handleLeadingMouseDown()"
        (mouseup)="handleLeadingMouseUp()"
        (mouseleave)="handleLeadingMouseLeave()"
        (touchstart)="handleLeadingTouchStart()"
        (touchend)="handleLeadingTouchEnd()"
        (keydown)="handleLeadingKeyDown($event)"
        (keyup)="handleLeadingKeyUp($event)"
      >
        @if (icon()) {
          <span class="material-symbols-outlined icon" aria-hidden="true">
            {{ icon() }}
          </span>
        }
        <span class="label">{{ label() }}</span>
      </button>

      <span
        class="divider"
        [class.disabled]="disabled() && menuDisabled()"
      ></span>

      <button
        #trailingButton
        type="button"
        class="trailing-button"
        [class.pressing]="isTrailingPressing()"
        [class.press-release]="isTrailingReleasing()"
        [class.menu-open]="isMenuOpen()"
        [disabled]="disabled() || menuDisabled()"
        [attr.aria-label]="menuAriaLabel()"
        aria-haspopup="menu"
        [attr.aria-expanded]="isMenuOpen()"
        (click)="onTrailingClick()"
        (mousedown)="handleTrailingMouseDown()"
        (mouseup)="handleTrailingMouseUp()"
        (mouseleave)="handleTrailingMouseLeave()"
        (touchstart)="handleTrailingTouchStart()"
        (touchend)="handleTrailingTouchEnd()"
        (keydown)="handleTrailingKeyDown($event)"
        (keyup)="handleTrailingKeyUp($event)"
      >
        <span
          class="material-symbols-outlined dropdown-icon"
          aria-hidden="true"
        >
          arrow_drop_down
        </span>
      </button>
    </div>
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }

      @keyframes press-ripple-spread {
        0% {
          transform: scale(0.6);
          opacity: 0;
        }
        100% {
          transform: scale(1);
          opacity: 0.1;
        }
      }

      @keyframes press-ripple-fade {
        0% {
          opacity: 0.1;
        }
        100% {
          opacity: 0;
        }
      }

      .m3-split-button {
        display: inline-flex;
        align-items: stretch;
        position: relative;
        border-radius: var(--button-border-radius-resting, 20px);
        transition:
          box-shadow var(--md-comp-button-motion-elevation),
          border-radius var(--md-comp-button-motion-shape-morph);
      }

      .leading-button,
      .trailing-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border: none;
        cursor: pointer;
        position: relative;
        font-family: inherit;
        font-weight: 500;
        box-sizing: border-box;
        transition:
          background-color var(--md-comp-button-motion-state-layer),
          color var(--md-comp-button-motion-state-layer),
          border-radius var(--md-comp-button-motion-shape-morph),
          transform var(--md-comp-button-motion-shape-morph);

        &::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          opacity: 0;
          pointer-events: none;
          transform: scale(1);
        }

        &:focus-visible {
          outline: none;
          z-index: 1;
          box-shadow:
            0 0 0 2px var(--md-sys-color-surface),
            0 0 0 4px var(--md-sys-color-secondary);
        }

        &:focus:not(:focus-visible) {
          outline: none;
        }
      }

      .leading-button {
        gap: 8px;
        border-top-left-radius: inherit;
        border-bottom-left-radius: inherit;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
      }

      .trailing-button {
        border-top-right-radius: inherit;
        border-bottom-right-radius: inherit;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        flex-shrink: 0;
      }

      .divider {
        width: 1px;
        align-self: stretch;
        flex-shrink: 0;
      }

      .icon {
        flex-shrink: 0;
      }

      .label {
        white-space: nowrap;
      }

      .dropdown-icon {
        transition: transform 200ms var(--md-sys-motion-easing-standard);
      }

      .trailing-button.menu-open .dropdown-icon {
        transform: rotate(180deg);
      }

      .material-symbols-outlined {
        font-variation-settings:
          'FILL' 0,
          'wght' 400,
          'GRAD' 0,
          'opsz' var(--icon-opsz, 24);
        transition: font-variation-settings
          var(--md-sys-motion-expressive-effects-fast);
      }

      // Size variants
      .size-xs {
        --button-border-radius-resting: 16px;
        --button-border-radius-pressed: 8px;
        --icon-opsz: 20;

        .leading-button {
          min-height: 28px;
          padding: 0 10px;
          font-size: 12px;
          line-height: 16px;

          .material-symbols-outlined {
            font-size: 16px;
          }
        }

        .trailing-button {
          min-height: 28px;
          padding: 0 6px;

          .material-symbols-outlined {
            font-size: 18px;
          }
        }
      }

      .size-sm {
        --button-border-radius-resting: 18px;
        --button-border-radius-pressed: 8px;
        --icon-opsz: 20;

        .leading-button {
          min-height: 32px;
          padding: 0 14px;
          font-size: 13px;
          line-height: 18px;

          .material-symbols-outlined {
            font-size: 18px;
          }
        }

        .trailing-button {
          min-height: 32px;
          padding: 0 8px;

          .material-symbols-outlined {
            font-size: 20px;
          }
        }
      }

      .size-default {
        --button-border-radius-resting: 20px;
        --button-border-radius-pressed: 10px;
        --icon-opsz: 24;

        .leading-button {
          min-height: 40px;
          padding: 0 20px;
          font-size: 14px;
          line-height: 20px;

          .material-symbols-outlined {
            font-size: 20px;
          }
        }

        .trailing-button {
          min-height: 40px;
          padding: 0 10px;

          .material-symbols-outlined {
            font-size: 24px;
          }
        }
      }

      .size-lg {
        --button-border-radius-resting: 24px;
        --button-border-radius-pressed: 12px;
        --icon-opsz: 24;

        .leading-button {
          min-height: 48px;
          padding: 0 28px;
          font-size: 16px;
          line-height: 24px;

          .material-symbols-outlined {
            font-size: 24px;
          }
        }

        .trailing-button {
          min-height: 48px;
          padding: 0 12px;

          .material-symbols-outlined {
            font-size: 28px;
          }
        }
      }

      .size-xl {
        --button-border-radius-resting: 28px;
        --button-border-radius-pressed: 14px;
        --icon-opsz: 28;

        .leading-button {
          min-height: 56px;
          padding: 0 36px;
          font-size: 18px;
          line-height: 28px;

          .material-symbols-outlined {
            font-size: 28px;
          }
        }

        .trailing-button {
          min-height: 56px;
          padding: 0 14px;

          .material-symbols-outlined {
            font-size: 32px;
          }
        }
      }

      // Variant: Elevated
      .variant-elevated {
        box-shadow:
          0 1px 2px 0 rgba(0, 0, 0, 0.3),
          0 1px 3px 1px rgba(0, 0, 0, 0.15);

        .leading-button,
        .trailing-button {
          background: var(--md-sys-color-surface-container);
          color: var(--md-sys-color-primary);

          &::before {
            background: var(--md-sys-color-primary);
          }

          &:hover:not(:disabled) {
            &::before {
              opacity: 0.08;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 500,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.pressing:not(:disabled) {
            &::before {
              opacity: 0.1;
              animation: press-ripple-spread 150ms
                var(--md-sys-motion-easing-expressive) forwards;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 300,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.press-release:not(:disabled) {
            &::before {
              animation: press-ripple-fade 200ms
                var(--md-sys-motion-easing-standard) forwards;
            }
          }

          &:focus-visible:not(:disabled) {
            &::before {
              opacity: 0.1;
            }
          }
        }

        .divider {
          background: var(--md-sys-color-outline-variant);
        }

        &:hover {
          box-shadow:
            0 2px 6px 2px rgba(0, 0, 0, 0.15),
            0 1px 2px 0 rgba(0, 0, 0, 0.3);
        }
      }

      // Variant: Filled (default)
      .variant-filled {
        .leading-button,
        .trailing-button {
          background: var(--md-sys-color-primary);
          color: var(--md-sys-color-on-primary);

          &::before {
            background: var(--md-sys-color-on-primary);
          }

          &:hover:not(:disabled) {
            &::before {
              opacity: 0.08;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 500,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.pressing:not(:disabled) {
            &::before {
              opacity: 0.1;
              animation: press-ripple-spread 150ms
                var(--md-sys-motion-easing-expressive) forwards;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 300,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.press-release:not(:disabled) {
            &::before {
              animation: press-ripple-fade 200ms
                var(--md-sys-motion-easing-standard) forwards;
            }
          }

          &:focus-visible:not(:disabled) {
            &::before {
              opacity: 0.1;
            }
          }
        }

        .divider {
          background: rgba(from var(--md-sys-color-on-primary) r g b / 0.2);
        }

        &:hover {
          box-shadow:
            0 1px 2px 0 rgba(0, 0, 0, 0.3),
            0 1px 3px 1px rgba(0, 0, 0, 0.15);
        }
      }

      // Variant: Tonal
      .variant-tonal {
        .leading-button,
        .trailing-button {
          background: var(--md-sys-color-secondary-container);
          color: var(--md-sys-color-on-secondary-container);

          &::before {
            background: var(--md-sys-color-on-secondary-container);
          }

          &:hover:not(:disabled) {
            &::before {
              opacity: 0.08;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 500,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.pressing:not(:disabled) {
            &::before {
              opacity: 0.1;
              animation: press-ripple-spread 150ms
                var(--md-sys-motion-easing-expressive) forwards;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 300,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.press-release:not(:disabled) {
            &::before {
              animation: press-ripple-fade 200ms
                var(--md-sys-motion-easing-standard) forwards;
            }
          }

          &:focus-visible:not(:disabled) {
            &::before {
              opacity: 0.1;
            }
          }
        }

        .divider {
          background: rgba(
            from var(--md-sys-color-on-secondary-container) r g b / 0.2
          );
        }

        &:hover {
          box-shadow:
            0 1px 2px 0 rgba(0, 0, 0, 0.3),
            0 1px 3px 1px rgba(0, 0, 0, 0.15);
        }
      }

      // Variant: Outlined
      .variant-outlined {
        border: 1px solid var(--md-sys-color-outline-variant);

        .leading-button,
        .trailing-button {
          background: transparent;
          color: var(--md-sys-color-primary);

          &::before {
            background: var(--md-sys-color-primary);
          }

          &:hover:not(:disabled) {
            &::before {
              opacity: 0.08;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 500,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.pressing:not(:disabled) {
            &::before {
              opacity: 0.1;
              animation: press-ripple-spread 150ms
                var(--md-sys-motion-easing-expressive) forwards;
            }

            .material-symbols-outlined {
              font-variation-settings:
                'FILL' 0,
                'wght' 300,
                'GRAD' 0,
                'opsz' var(--icon-opsz);
            }
          }

          &.press-release:not(:disabled) {
            &::before {
              animation: press-ripple-fade 200ms
                var(--md-sys-motion-easing-standard) forwards;
            }
          }

          &:focus-visible:not(:disabled) {
            &::before {
              opacity: 0.1;
            }
          }
        }

        .divider {
          background: var(--md-sys-color-outline-variant);
        }
      }

      // Disabled states
      .leading-button:disabled,
      .trailing-button:disabled {
        cursor: not-allowed;

        &::before {
          display: none !important;
        }
      }

      .variant-elevated,
      .variant-filled,
      .variant-tonal {
        .leading-button:disabled,
        .trailing-button:disabled {
          background: rgba(
            from var(--md-sys-color-on-surface) r g b / 0.12
          ) !important;
          color: rgba(
            from var(--md-sys-color-on-surface) r g b / 0.38
          ) !important;
        }
      }

      .variant-outlined {
        .leading-button:disabled,
        .trailing-button:disabled {
          background: transparent !important;
          color: rgba(
            from var(--md-sys-color-on-surface) r g b / 0.38
          ) !important;
        }

        &:has(.leading-button:disabled):has(.trailing-button:disabled) {
          border-color: rgba(
            from var(--md-sys-color-on-surface) r g b / 0.12
          ) !important;
        }
      }

      .divider.disabled {
        background: rgba(
          from var(--md-sys-color-on-surface) r g b / 0.12
        ) !important;
      }

      // Shape morphing on press
      .leading-button.pressing,
      .trailing-button.pressing {
        transform: scale(0.98);
      }

      @media (prefers-reduced-motion: reduce) {
        .m3-split-button,
        .leading-button,
        .trailing-button {
          transition: none;

          &::before {
            animation: none !important;
          }

          &.pressing {
            transform: none;
          }
        }

        .dropdown-icon {
          transition: none;
        }

        .material-symbols-outlined {
          transition: none;
        }
      }
    `,
  ],
})
export class M3SplitButtonComponent {
  label = input.required<string>();
  icon = input<string>();
  variant = input<M3SplitButtonVariant>('filled');
  size = input<M3SplitButtonSize>('default');
  disabled = input<boolean>(false);
  menuDisabled = input<boolean>(false);
  ariaLabel = input<string>();
  menuAriaLabel = input<string>('More options');
  menuTrigger = input<MatMenuTrigger>();

  clicked = output<MouseEvent>();
  trailingClicked = output<void>();

  private readonly trailingButtonRef =
    viewChild<ElementRef<HTMLButtonElement>>('trailingButton');

  private isLeadingPressingSignal = signal(false);
  private isLeadingReleasingSignal = signal(false);
  private isTrailingPressingSignal = signal(false);
  private isTrailingReleasingSignal = signal(false);
  private isMenuOpenSignal = signal(false);

  readonly isLeadingPressing = this.isLeadingPressingSignal.asReadonly();
  readonly isLeadingReleasing = this.isLeadingReleasingSignal.asReadonly();
  readonly isTrailingPressing = this.isTrailingPressingSignal.asReadonly();
  readonly isTrailingReleasing = this.isTrailingReleasingSignal.asReadonly();
  readonly isMenuOpen = this.isMenuOpenSignal.asReadonly();

  constructor() {
    effect((onCleanup) => {
      const trigger = this.menuTrigger();
      if (trigger) {
        const sub = new Subscription();
        sub.add(
          trigger.menuOpened.subscribe(() => this.isMenuOpenSignal.set(true))
        );
        sub.add(
          trigger.menuClosed.subscribe(() => {
            this.isMenuOpenSignal.set(false);
            this.trailingButtonRef()?.nativeElement.focus();
          })
        );

        onCleanup(() => {
          sub.unsubscribe();
          this.isMenuOpenSignal.set(false);
        });
      }
    });
  }

  containerClass = computed(() => {
    return [
      'm3-split-button',
      `size-${this.size()}`,
      `variant-${this.variant()}`,
    ].join(' ');
  });

  onPrimaryClick(event: MouseEvent): void {
    if (!this.disabled()) {
      this.clicked.emit(event);
    }
  }

  onTrailingClick(): void {
    if (!this.disabled() && !this.menuDisabled()) {
      this.trailingClicked.emit();
    }
  }

  handleLeadingMouseDown(): void {
    if (!this.disabled()) {
      this.isLeadingPressingSignal.set(true);
    }
  }

  handleLeadingMouseUp(): void {
    this.isLeadingPressingSignal.set(false);
    this.isLeadingReleasingSignal.set(true);
    setTimeout(() => {
      this.isLeadingReleasingSignal.set(false);
    }, 350);
  }

  handleLeadingMouseLeave(): void {
    if (this.isLeadingPressingSignal()) {
      this.isLeadingPressingSignal.set(false);
      this.isLeadingReleasingSignal.set(true);
      setTimeout(() => {
        this.isLeadingReleasingSignal.set(false);
      }, 350);
    }
  }

  handleLeadingTouchStart(): void {
    if (!this.disabled()) {
      this.isLeadingPressingSignal.set(true);
    }
  }

  handleLeadingTouchEnd(): void {
    this.isLeadingPressingSignal.set(false);
    this.isLeadingReleasingSignal.set(true);
    setTimeout(() => {
      this.isLeadingReleasingSignal.set(false);
    }, 350);
  }

  handleLeadingKeyDown(event: KeyboardEvent): void {
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      !event.repeat &&
      !this.disabled()
    ) {
      this.isLeadingPressingSignal.set(true);
    }
  }

  handleLeadingKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      this.isLeadingPressingSignal.set(false);
      this.isLeadingReleasingSignal.set(true);
      setTimeout(() => {
        this.isLeadingReleasingSignal.set(false);
      }, 350);
    }
  }

  handleTrailingMouseDown(): void {
    if (!this.disabled() && !this.menuDisabled()) {
      this.isTrailingPressingSignal.set(true);
    }
  }

  handleTrailingMouseUp(): void {
    this.isTrailingPressingSignal.set(false);
    this.isTrailingReleasingSignal.set(true);
    setTimeout(() => {
      this.isTrailingReleasingSignal.set(false);
    }, 350);
  }

  handleTrailingMouseLeave(): void {
    if (this.isTrailingPressingSignal()) {
      this.isTrailingPressingSignal.set(false);
      this.isTrailingReleasingSignal.set(true);
      setTimeout(() => {
        this.isTrailingReleasingSignal.set(false);
      }, 350);
    }
  }

  handleTrailingTouchStart(): void {
    if (!this.disabled() && !this.menuDisabled()) {
      this.isTrailingPressingSignal.set(true);
    }
  }

  handleTrailingTouchEnd(): void {
    this.isTrailingPressingSignal.set(false);
    this.isTrailingReleasingSignal.set(true);
    setTimeout(() => {
      this.isTrailingReleasingSignal.set(false);
    }, 350);
  }

  handleTrailingKeyDown(event: KeyboardEvent): void {
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      !event.repeat &&
      !this.disabled() &&
      !this.menuDisabled()
    ) {
      this.isTrailingPressingSignal.set(true);
    }
  }

  handleTrailingKeyUp(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      this.isTrailingPressingSignal.set(false);
      this.isTrailingReleasingSignal.set(true);
      setTimeout(() => {
        this.isTrailingReleasingSignal.set(false);
      }, 350);
    }
  }
}
